import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AnyComponentData, CSVDestinationType } from '../../package.models';
import { ComponentTypeItem } from '../../../constants/component_types';
import { COMPONENT_TYPE } from '../../../constants/component_type';
import { updateComponent, updateRawComponent } from '../../store/component.actions';
import { AppState } from '../../../store';

@Component({
  selector: 'cloud-storage-delimiter',
  template: `
    <div class="cloud-storage-delimiter">
      <label>{{ 'cloud-storage-source-editor.form.labels.delimiter_properties' | translate }}</label>
      <xp-form-group>
        <label for="field_delimiter">{{ 'cloud-storage-source-editor.form.labels.field_delimiter' | translate }}</label>
        <xp-select
          id="field_delimiter"
          name="field_delimiter"
          class="form-control xp-select"
          [value]="fieldDelimiter"
          (valueChange)="onFieldDelimiterChange($event)"
          [options]="fieldDelimiterOptions"
          [preventEmpty]="true"
        ></xp-select>
      </xp-form-group>
      <xp-form-group *ngIf="showCustomDelimiterInput">
        <label for="custom_field_delimiter">{{
          'cloud-storage-source-editor.form.labels.field_delimiter_custom' | translate
        }}</label>
        <xp-input
          id="custom_field_delimiter"
          name="custom_field_delimiter"
          [ngModel]="customDelimiter"
          class="form-control"
          (ngModelChange)="onCustomDelimiterChange(customDelimiter)"
          type="text"
        ></xp-input>
        <div class="error-message" *ngIf="showDelimiterErrorMessage">
          <span>{{ 'cloud-storage-source-editor.form.labels.field_delimiter_custom_error' | translate }}</span>
        </div>
      </xp-form-group>
      <xp-form-group>
        <label for="text_qualifier">{{ 'cloud-storage-source-editor.form.labels.string_qualifier' | translate }}</label>
        <xp-select
          id="text_qualifier"
          name="text_qualifier"
          class="form-control xp-select"
          [value]="settings.text_qualifier"
          (valueChange)="onTextQualifierChange($event)"
          [options]="textQualifierOptions"
          [preventEmpty]="true"
        ></xp-select>
      </xp-form-group>
      <xp-form-group id="escape-character-container" *ngIf="isEscapeCharacterDropdownVisible">
        <label for="escape_character">{{
          'cloud-storage-destination-editor.labels.escape_character' | translate
        }}</label>
        <xp-select
          id="escape_character"
          name="escape_character"
          class="form-control xp-select"
          [value]="settings.escape_character"
          (valueChange)="onEscapeCharacterChange($event)"
          [options]="escapeCharacterOptions"
        ></xp-select>
        <small *ngIf="component && component.componentType === 'cloud_storage_destination_component'">{{
          'cloud-storage-destination-editor.hints.escape_character' | translate
        }}</small>
        <small *ngIf="component && component.componentType === 'cloud_storage_source_component'">{{
          'cloud-storage-source-editor.hints.escape_character' | translate
        }}</small>
      </xp-form-group>
      <div *ngIf="isLineEndingLabelVisible">
        <label>{{ 'cloud-storage-destination-editor.labels.line_ending' | translate }}</label>
        <div class="btn-group btn-group-md btn-group-select">
          <button
            type="button"
            class="btn btn-default"
            [ngClass]="{ 'active btn-primary': settings.line_ending == 'unix' }"
            (click)="settings.line_ending = 'unix'"
          >
            {{ 'cloud-storage-destination-editor.selects.line_ending.unix' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-default"
            [ngClass]="{ 'active btn-primary': settings.line_ending == 'windows' }"
            (click)="settings.line_ending = 'windows'"
          >
            {{ 'cloud-storage-destination-editor.selects.line_ending.windows' | translate }}
          </button>
        </div>
      </div>
      <div>
        <xp-input-checkbox
          [ngModel]="settings.write_header"
          *ngIf="component && component.componentType === 'cloud_storage_destination_component'"
          [labelText]="'cloud-storage-' + component.type + '-editor.form.checkbox' | translate"
          name="write_header"
        ></xp-input-checkbox>
        <xp-input-checkbox
          [ngModel]="settings.skip_header"
          *ngIf="component && component.componentType !== 'cloud_storage_destination_component'"
          [labelText]="'cloud-storage-' + component.type + '-editor.form.checkbox' | translate"
          name="skip_header"
        ></xp-input-checkbox>
      </div>
      <hr />
    </div>
  `,
})
export class CloudStorageDelimiterComponent implements OnInit {
  @Input() rawComponent: AnyComponentData;
  @Input() component: ComponentTypeItem;
  @Input() settings: CSVDestinationType;
  @Input() lineInside: boolean;

  showCustomDelimiterInput = false;
  customDelimiter = ',';
  fieldDelimiter = ',';
  textQualifier = '';
  escapeCharacter = '';
  lineEnding = '';
  writeHeader = false;
  skipHeader = false;
  isEscapeCharacterDropdownVisible = false;
  showDelimiterErrorMessage = false;

  fieldDelimiterOptions = [
    { value: ',', text: ',' },
    { value: '\\t', text: '\\t (Tab)' },
    { value: '\\u0001', text: '\\u0001' },
    { value: 'space', text: 'Space' },
    { value: '\\n', text: '\\n (New Line)' },
    { value: 'custom', text: 'Custom' },
  ];
  textQualifierOptions = [];
  escapeCharacterOptions = [
    { value: '"', text: '"' },
    { value: "'", text: "'" },
    { value: '\\', text: '\\' },
  ];

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.fieldDelimiter = this.settings.field_delimiter;
    this.textQualifier = this.settings.text_qualifier;
    this.escapeCharacter = this.settings.escape_character;
    this.lineEnding = this.settings.line_ending;

    if (
      this.fieldDelimiterOptions.find((option) => option.value === this.fieldDelimiter) ||
      this.fieldDelimiter === ' '
    ) {
      this.fieldDelimiter = this.fieldDelimiter === ' ' ? 'space' : this.fieldDelimiter;
    } else {
      this.fieldDelimiter = 'custom';
      this.customDelimiter = this.fieldDelimiter;
      this.showCustomDelimiterInput = true;
    }

    if (this.settings.write_header !== undefined) {
      this.writeHeader = this.settings.write_header;
    }

    if (this.settings.skip_header !== undefined) {
      this.skipHeader = this.settings.skip_header;
    }

    this.isEscapeCharacterDropdownVisible = this.settings.text_qualifier !== '';

    this.textQualifierOptions = [
      {
        value: '',
        text: 'cloud-storage-source-editor.form.selects.string_qualifier.options.none',
        translate: true,
      },
      {
        value: 'quoted_multi_line',
        text: 'cloud-storage-source-editor.form.selects.string_qualifier.options.double_quote_newline_inside',
        translate: true,
      },
      !this.isCloudStorageDestinationComponent ? { value: '"', text: '"' } : null,
      {
        value: 'single_quoted_multi_line',
        text: 'cloud-storage-source-editor.form.selects.string_qualifier.options.single_quote_newline_inside',
        translate: true,
      },
      !this.isCloudStorageDestinationComponent ? { value: "'", text: "'" } : null,
      { value: 'enforce_double_quotes', text: '"' },
      { value: 'enforce_single_quotes', text: "'" },
      this.isCloudStorageDestinationComponent
        ? {
            value: '"',
            text:
              'cloud-storage-destination-editor.form.selects.string_qualifier.options.double_quote_contains_delimiter_or_line_break',
            translate: true,
          }
        : null,
      this.isCloudStorageDestinationComponent
        ? {
            value: "'",
            text:
              'cloud-storage-destination-editor.form.selects.string_qualifier.options.single_quote_contains_delimiter_or_line_break',
            translate: true,
          }
        : null,
    ].filter(Boolean);

    if (this.settings.escape_character == null || this.settings.escape_character === 'none') {
      this.onSettingsValueChange('"', 'escape_character');
    }

    if (!this.fieldDelimiterOptions.find((item) => item.value === this.settings.field_delimiter)) {
      this.fieldDelimiterOptions = [
        ...this.fieldDelimiterOptions,
        {
          value: this.settings.field_delimiter,
          text: this.settings.field_delimiter,
        },
      ];
    }

    if (!this.escapeCharacterOptions.find((item) => item.value === this.settings.escape_character)) {
      this.escapeCharacterOptions = [
        ...this.escapeCharacterOptions,
        {
          value: this.settings.escape_character,
          text: this.settings.escape_character,
        },
      ];
    }

    if (!this.lineInside) {
      this.textQualifierOptions = this.textQualifierOptions.filter(
        (item) => item.value !== 'quoted_multi_line' && item.value !== 'single_quoted_multi_line',
      );
    } else {
      this.textQualifierOptions = this.textQualifierOptions.filter(
        (item) => item.value !== 'enforce_double_quotes' && item.value !== 'enforce_single_quotes',
      );
      this.escapeCharacterOptions = this.escapeCharacterOptions.filter((item) => item.value !== 'none');
    }

    if (this.settings && this.settings.field_delimiter === ' ') {
      this.onSettingsValueChange('space', 'field_delimiter');
    }
    if (this.settings && (this.settings.escape_character === 'none' || this.settings.escape_character === '')) {
      this.onSettingsValueChange('"', 'escape_character');
    }
    if (this.settings && this.settings.text_qualifier === 'none') {
      this.onSettingsValueChange('', 'text_qualifier');
    }
    if (!this.settings.line_ending) {
      this.onSettingsValueChange('unix', 'line_ending');
    }
  }

  get isLineEndingLabelVisible(): boolean {
    return (
      this.component &&
      this.component.componentType === COMPONENT_TYPE.CLOUD_STORAGE_DESTINATION_COMPONENT &&
      this.settings.text_qualifier !== ''
    );
  }

  onSettingsValueChange(value: any, key: string) {
    let newValue = value;

    if (key === 'field_delimiter' && value === 'space') {
      newValue = ' ';
    }

    if (key === 'escape_character' && (value === 'none' || value === '')) {
      newValue = '"';
    }

    if (key === 'text_qualifier' && value === 'none') {
      newValue = '';
    }

    const settings = {
      ...this.settings,
      [key]: newValue,
    };

    this.store.dispatch(
      updateRawComponent({
        rawComponent: { destination_type: { csv_destination_type: { ...settings } } },
      }),
    );
    this.store.dispatch(
      updateComponent({ component: { destination_type: { csv_destination_type: { ...settings } } } }),
    );
  }

  onFieldDelimiterChange(fieldDelimiterValue: string) {
    this.fieldDelimiter = fieldDelimiterValue;

    if (fieldDelimiterValue === 'custom') {
      this.showCustomDelimiterInput = true;
      this.onSettingsValueChange(this.customDelimiter, 'field_delimiter');
    } else {
      this.showCustomDelimiterInput = false;
      this.onSettingsValueChange(fieldDelimiterValue, 'field_delimiter');
    }
  }

  onCustomDelimiterChange(customDelimiter: string) {
    if (customDelimiter === undefined) {
      this.showDelimiterErrorMessage = true;
    } else {
      this.showDelimiterErrorMessage = false;
      this.customDelimiter = customDelimiter;
      this.onSettingsValueChange(customDelimiter, 'field_delimiter');
    }
  }

  onTextQualifierChange(textQualifierValue: string) {
    this.onSettingsValueChange(textQualifierValue, 'text_qualifier');
    this.isEscapeCharacterDropdownVisible = textQualifierValue !== '';
  }

  onEscapeCharacterChange(escapeCharacterValue: string) {
    this.onSettingsValueChange(escapeCharacterValue, 'escape_character');
  }

  get isCloudStorageDestinationComponent(): boolean {
    return this.component && this.component.componentType === COMPONENT_TYPE.CLOUD_STORAGE_DESTINATION_COMPONENT;
  }
}
